import { compressToEncodedURIComponent } from 'lz-string';

import { getContrastColor, isValidHexColor } from './colorUtils';
import {
  ANONYMOUS_ACCOUNT_NAME,
  DEFAULT_BACKGROUND_COLOR,
  IFRAME_BACKGROUND_COLOR
} from './constants';
import { DEFAULT_ACCOUNT_BACKGROUND_IMAGE } from './default-account-background-image';
import { DEFAULT_ACCOUNT_THEME } from './defaultAccountTheme';
import {
  DEFAULT_OPENGRAPH_IMAGE_DERIVATIVE,
  getPrimaryMedium,
  getStandardDerivative,
  getThumbnailPath,
  hasPrimaryMedium,
  hasStandardDerivative
} from './media-utils';
import { CitOrigin, FolderLayout, HeroField } from './types/cit-public-api';
import type {
  Account,
  AccountThemeConfig,
  Entry,
  HeaderHTML,
  HeroConfig,
  Media,
  MediaAdornment,
  MediaAspectRatio,
  MediaDerivative,
  Profile,
  Slot,
  ThumbnailSize
} from './types/cit-public-api';
import { getProperty } from './properties';
import { formatDate } from './format-utils';

// theme values are stored as plain numbers, but they're always consumed as CSS lengths,
// so keyword values ('normal', 'medium') pass through untouched and numbers get a px unit
// is important because plain html (like the CustomHeader) unlike React does not accept raw numbers as px lengths
const withPx = (value: number | string): string =>
  typeof value === 'number' ? `${value}px` : value;

// the intrinsic size of the bundled default background
const DEFAULT_ACCOUNT_BACKGROUND_DERIVATIVE: MediaDerivative = {
  path: DEFAULT_ACCOUNT_BACKGROUND_IMAGE,
  width: 1599,
  height: 1024
};

export default class AccountTheme {
  constructor(
    private readonly citOrigin: CitOrigin,
    private readonly account?: Account,
    private readonly theme?: AccountThemeConfig
  ) {}

  bodyFont(): string {
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return DEFAULT_ACCOUNT_THEME.typography.bodyFont;
    }
    return (
      this.theme?.typography?.bodyFont ??
      this.account?.theme?.typography?.bodyFont ??
      DEFAULT_ACCOUNT_THEME.typography.bodyFont
    );
  }

  titleFont(): string {
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return DEFAULT_ACCOUNT_THEME.typography.titleFont;
    }
    return (
      this.theme?.typography?.titleFont ??
      this.account?.theme?.typography?.titleFont ??
      DEFAULT_ACCOUNT_THEME.typography.titleFont
    );
  }

  bodyFontColor(entry?: Entry | Profile): string {
    const fallback = getContrastColor(this.backgroundColor(entry, !entry));
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return fallback;
    }
    return (
      this.theme?.color?.bodyFontColor ?? this.account?.theme?.color?.bodyFontColor ?? fallback
    );
  }

  titleFontColor(entry?: Entry | Profile): string {
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return getContrastColor(this.backgroundColor(entry, !entry));
    }
    // the title falls back to the body font color before the contrast-based default
    return (
      this.theme?.color?.titleFontColor ??
      this.account?.theme?.color?.titleFontColor ??
      this.bodyFontColor(entry)
    );
  }

  // header theming is a HUB Pro-only customization; everywhere else always gets the default header
  headerFont(): string {
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return DEFAULT_ACCOUNT_THEME.header.headerFont;
    }
    return (
      this.theme?.header?.headerFont ??
      this.account?.theme?.header?.headerFont ??
      DEFAULT_ACCOUNT_THEME.header.headerFont
    );
  }

  headerFontColor(): string {
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return DEFAULT_ACCOUNT_THEME.header.headerFontColor;
    }
    return (
      this.theme?.header?.headerFontColor ??
      this.account?.theme?.header?.headerFontColor ??
      DEFAULT_ACCOUNT_THEME.header.headerFontColor
    );
  }

  headerBackgroundColor(): string {
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return DEFAULT_ACCOUNT_THEME.header.headerBackgroundColor;
    }
    return (
      this.theme?.header?.headerBackgroundColor ??
      this.account?.theme?.header?.headerBackgroundColor ??
      DEFAULT_ACCOUNT_THEME.header.headerBackgroundColor
    );
  }

  // the account's avatar as identity rather than a hero field, so it ignores hero visibility: the
  // CustomHeader still shows the logo for an account that hid the avatar in its hero. The HUB is
  // sent no flat avatar, so fall back to the hero item's value there -- not getHeroFieldValue,
  // though the api only sends that value when it is visible anyway
  icon(): Media | undefined {
    return this.account?.avatar ?? this.getHeroField(HeroField.AVATAR)?.value;
  }

  // icon() as a thumbnail path. Empty rather than the placeholder when there is no avatar, so
  // callers can tell the two apart; pass it as a fallback path with `|| undefined` to keep a
  // caller's own placeholder
  iconURL(): string {
    return getThumbnailPath(this.icon(), '');
  }

  // the hero's avatar, which an account can hide per origin. Falls back to the account's avatar,
  // since an account that never overrode the image has only visibility on the hero field
  avatarURL(): string {
    return this.isHeroFieldVisible(HeroField.AVATAR)
      ? getThumbnailPath(this.getHeroFieldValue(HeroField.AVATAR) ?? this.account?.avatar, '')
      : '';
  }

  // HUB Pro is branded on the account's own subdomain so it can't be anonymous, and always shows
  // the name. Elsewhere the NAME toggle is privacy, so the placeholder stands in everywhere and a
  // withheld name reads the same as one that was never set
  accountName(): string {
    // not getHeroFieldValue, which reports a hidden field as unset
    const name = this.getHeroField(HeroField.NAME)?.value ?? this.account?.name ?? '';

    if (this.citOrigin === CitOrigin.HUBPRO) {
      return name || ANONYMOUS_ACCOUNT_NAME;
    }

    return this.isHeroFieldVisible(HeroField.NAME) && name ? name : ANONYMOUS_ACCOUNT_NAME;
  }

  // the hero's label. On HUB Pro the NAME toggle collapses just this, since a branded subdomain
  // cannot be anonymous; everywhere else the toggle is privacy, so this matches accountName()
  heroName(): string {
    if (this.citOrigin === CitOrigin.HUBPRO && !this.isHeroFieldVisible(HeroField.NAME)) {
      return '';
    }

    return this.accountName();
  }

  description(): string {
    return this.getHeroFieldValue(HeroField.DESCRIPTION) ?? '';
  }

  location(): string {
    return this.getHeroFieldValue(HeroField.LOCATION) ?? '';
  }

  total(): number {
    return this.getHeroFieldValue(HeroField.TOTAL) ?? 0;
  }

  headerHTML(): HeaderHTML {
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return DEFAULT_ACCOUNT_THEME.header.headerHTML;
    }

    const headerHTML =
      this.theme?.header?.headerHTML ??
      // we only want to fall back to the default theme headerHTML when in the theme tester, so setting it to undefined shows in the preview
      (!this.isTestTheme() ? this.account?.theme?.header?.headerHTML : undefined);

    return headerHTML ?? DEFAULT_ACCOUNT_THEME.header.headerHTML;
  }

  lineHeight(): string {
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return withPx(DEFAULT_ACCOUNT_THEME.typography.lineHeight);
    }
    return withPx(
      this.theme?.typography?.lineHeight ??
        this.account?.theme?.typography?.lineHeight ??
        DEFAULT_ACCOUNT_THEME.typography.lineHeight
    );
  }

  letterSpacing(): string {
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return withPx(DEFAULT_ACCOUNT_THEME.typography.letterSpacing);
    }
    return withPx(
      this.theme?.typography?.letterSpacing ??
        this.account?.theme?.typography?.letterSpacing ??
        DEFAULT_ACCOUNT_THEME.typography.letterSpacing
    );
  }

  background(): Media | undefined {
    return this.getHeroFieldValue(HeroField.BACKGROUND);
  }

  // a hidden or unset background still yields the default image, so callers that need to know
  // whether there is a background at all check background() instead
  backgroundURL(): string {
    return getStandardDerivative(this.background(), DEFAULT_ACCOUNT_BACKGROUND_DERIVATIVE).path;
  }

  // the OG image for a page: the page's own medium, else the account's hero background, else
  // the platform default. HUB Pro serves no platform default, so it gets none there. Not a hero
  // getter -- OG cards are not the hero
  openGraphDerivative(medium?: Media): MediaDerivative | undefined {
    const background = this.background();

    if (hasStandardDerivative(medium)) {
      return getStandardDerivative(medium);
    }

    if (hasStandardDerivative(background)) {
      return getStandardDerivative(background);
    }

    return this.citOrigin === CitOrigin.HUBPRO ? undefined : DEFAULT_OPENGRAPH_IMAGE_DERIVATIVE;
  }

  backgroundColor(entry?: Entry | Profile, useAccountBackground?: boolean): string {
    if (this.citOrigin === CitOrigin.IFRAME) {
      return `#${IFRAME_BACKGROUND_COLOR}`;
    }
    const backgroundColorSource =
      // an undefined background color signifies it should be auto, so if we're in a test theme returning undefined is fine
      this.isTestTheme()
        ? (this.theme?.color?.backgroundColor ?? this.theme?.backgroundColor)
        : (this.account?.theme?.color?.backgroundColor ?? this.account?.theme?.backgroundColor);

    if (backgroundColorSource) {
      return isValidHexColor(backgroundColorSource)
        ? // TODO: remove the replace once we migrate themes
          `#${backgroundColorSource.replace('#', '')}`
        : `#${DEFAULT_BACKGROUND_COLOR}`;
    }
    const accountBackgroundColor = this.background()?.derivatives?.dominant_color;

    if (useAccountBackground && accountBackgroundColor) {
      // TODO: fix needing to have to add the hash here
      return `#${accountBackgroundColor}`;
    }
    if (
      !useAccountBackground &&
      entry &&
      hasPrimaryMedium(entry?.media) &&
      getPrimaryMedium(entry?.media)?.derivatives?.dominant_color
    ) {
      const primaryMedium = getPrimaryMedium(entry?.media);
      return primaryMedium?.derivatives?.dominant_color
        ? `#${primaryMedium.derivatives.dominant_color}`
        : `#${DEFAULT_BACKGROUND_COLOR}`;
    }

    return `#${DEFAULT_BACKGROUND_COLOR}`;
  }

  baseFontSize(): string {
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return withPx(DEFAULT_ACCOUNT_THEME.typography.baseFontSize);
    }

    return withPx(
      this.theme?.typography?.baseFontSize ??
        this.account?.theme?.typography?.baseFontSize ??
        DEFAULT_ACCOUNT_THEME.typography.baseFontSize
    );
  }

  bannerTextColor(): string {
    // TODO: remove theme.bannerTextColor references once we migrate banner text color to color object
    const bannerTextColor =
      this.theme?.color?.bannerTextColor ??
      this.theme?.bannerTextColor ??
      this.account?.theme?.color?.bannerTextColor ??
      this.account?.theme?.bannerTextColor ??
      this.titleFontColor();

    // remove once we migrate banner text color to color object, then # should never be present
    const normalizedBannerTextColor = `#${bannerTextColor.replace('#', '')}`;

    return normalizedBannerTextColor;
  }

  mediaAdornment(): MediaAdornment {
    return (
      this.theme?.style?.mediaAdornment ??
      this.account?.theme?.style?.mediaAdornment ??
      this.account?.media?.media_adornment ??
      DEFAULT_ACCOUNT_THEME.style.mediaAdornment
    );
  }

  mediaAspectRatio(): MediaAspectRatio {
    return (
      this.theme?.style?.mediaAspectRatio ??
      this.account?.theme?.style?.mediaAspectRatio ??
      this.account?.media?.media_aspect_ratio ??
      DEFAULT_ACCOUNT_THEME.style.mediaAspectRatio
    );
  }

  entryCardColor(): string | undefined {
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return;
    }

    return this.theme?.color?.entryCardColor ?? this.account?.theme?.color?.entryCardColor;
  }

  folderLayout(): FolderLayout {
    // row layout is a HUB Pro-only customization; everywhere else is always a grid
    if (this.citOrigin !== CitOrigin.HUBPRO) {
      return FolderLayout.GRID;
    }
    return (
      this.theme?.layout?.folderLayout ??
      this.account?.theme?.layout?.folderLayout ??
      DEFAULT_ACCOUNT_THEME.layout.folderLayout
    );
  }

  // callers that lay out with the gap arithmetically (rather than handing it to CSS) pass includePx: false
  gap(includePx?: true): string;
  gap(includePx: false): number;
  gap(includePx = true): string | number {
    const gap =
      this.theme?.layout?.gap ??
      this.account?.theme?.layout?.gap ??
      DEFAULT_ACCOUNT_THEME.layout.gap;

    return includePx ? withPx(gap) : gap;
  }

  thumbnailSize(): ThumbnailSize {
    return (
      this.theme?.layout?.thumbnailSizes ??
      this.account?.theme?.layout?.thumbnailSizes ??
      DEFAULT_ACCOUNT_THEME.layout.thumbnailSizes
    );
  }

  // websiteURL() always resolves to somewhere (the HUB home page when there is no website), so
  // callers that need to know whether the account gave one ask here
  hasWebsiteURL(): boolean {
    return Boolean(this.getHeroFieldValue(HeroField.WEBSITE_URL)?.trim());
  }

  websiteURL(): string {
    const websiteURL = this.getHeroFieldValue(HeroField.WEBSITE_URL);

    if (!websiteURL) {
      // no website to link out to, so fall back to the HUB home page
      return this.isTestTheme() ? `/?testTheme=${this.getCompressedThemeConfig()}` : '/';
    }

    // without a scheme (google.com) the browser treats the href as a path relative to the current page,
    // so add one unless it already has a scheme (https:, mailto:) or is protocol-relative (//google.com)
    return /^([a-z][a-z0-9+.-]*:|\/\/)/i.test(websiteURL) ? websiteURL : `https://${websiteURL}`;
  }

  // OLD SLOTS FALLBACK — remove this whole method once all themes migrate to the new thumbnailSlots shape.
  // In the old shape (account.customization.thumbnail) a slot being defined meant it was "on",
  // and the value itself was the property path.
  private getOldThumbnailSlot(slot: 'slot1' | 'slot2' | 'slot3'): Slot | undefined {
    const oldSlot = this.account?.customization?.thumbnail?.[slot];
    return oldSlot !== undefined ? { on: true, property: oldSlot } : undefined;
  }
  // END OLD SLOTS FALLBACK

  getThumbnailSlotValues(entry: Entry) {
    const { properties } = entry;

    const enrichedProperties = {
      ...properties,
      hasClass: { value_text: entry.classification }
    };

    const { renderSlot1, renderSlot2, renderSlot3, renderSlot4 } = this.getRenderedThumbnailSlots();

    const slot1Path = renderSlot1
      ? (this?.theme?.layout?.thumbnailSlots?.slot1?.property ??
        this?.account?.theme?.layout?.thumbnailSlots?.slot1?.property ??
        this.getOldThumbnailSlot('slot1')?.property ?? // OLD SLOTS FALLBACK
        DEFAULT_ACCOUNT_THEME.layout.thumbnailSlots.slot1.property)
      : undefined;

    const slot2Path = renderSlot2
      ? (this?.theme?.layout?.thumbnailSlots?.slot2?.property ??
        this?.account?.theme?.layout?.thumbnailSlots?.slot2?.property ??
        this.getOldThumbnailSlot('slot2')?.property ?? // OLD SLOTS FALLBACK
        DEFAULT_ACCOUNT_THEME.layout.thumbnailSlots.slot2.property)
      : undefined;

    const slot3Path = renderSlot3
      ? (this?.theme?.layout?.thumbnailSlots?.slot3?.property ??
        this?.account?.theme?.layout?.thumbnailSlots?.slot3?.property ??
        this.getOldThumbnailSlot('slot3')?.property) // OLD SLOTS FALLBACK
      : undefined;

    const slot4Path = renderSlot4
      ? (this?.theme?.layout?.thumbnailSlots?.slot4?.property ??
        this?.account?.theme?.layout?.thumbnailSlots?.slot4?.property)
      : undefined;

    const formatIfDate = (property: string | undefined, path: string[]) => {
      if (!property) {
        return ' ';
      }

      return path[path.length - 1] === 'value_date' ? formatDate(property) : property;
    };

    return [
      slot1Path ? formatIfDate(getProperty(enrichedProperties, slot1Path), slot1Path) : ' ',
      slot2Path ? formatIfDate(getProperty(enrichedProperties, slot2Path), slot2Path) : ' ',
      slot3Path ? formatIfDate(getProperty(enrichedProperties, slot3Path), slot3Path) : ' ',
      slot4Path ? formatIfDate(getProperty(enrichedProperties, slot4Path), slot4Path) : ' '
    ];
  }

  getRenderedThumbnailSlots() {
    return {
      renderSlot1:
        this?.theme?.layout?.thumbnailSlots?.slot1?.on ??
        this?.account?.theme?.layout?.thumbnailSlots?.slot1?.on ??
        this.getOldThumbnailSlot('slot1')?.on ?? // OLD SLOTS FALLBACK
        DEFAULT_ACCOUNT_THEME.layout.thumbnailSlots.slot1.on,
      renderSlot2:
        this?.theme?.layout?.thumbnailSlots?.slot2?.on ??
        this?.account?.theme?.layout?.thumbnailSlots?.slot2?.on ??
        this.getOldThumbnailSlot('slot2')?.on ?? // OLD SLOTS FALLBACK
        DEFAULT_ACCOUNT_THEME.layout.thumbnailSlots.slot2.on,
      renderSlot3:
        this?.theme?.layout?.thumbnailSlots?.slot3?.on ??
        this?.account?.theme?.layout?.thumbnailSlots?.slot3?.on ??
        this.getOldThumbnailSlot('slot3')?.on ?? // OLD SLOTS FALLBACK
        false,
      renderSlot4:
        this?.theme?.layout?.thumbnailSlots?.slot4?.on ??
        this?.account?.theme?.layout?.thumbnailSlots?.slot4?.on ??
        false
    };
  }

  private getHeroField<K extends HeroField>(field: K): HeroConfig[K] {
    // the tester only sends a hero field once it differs from the account's, so a field it left
    // out falls back to the account's whole item. The fallback is all-or-nothing on purpose: an
    // item the tester did send is the truth for both its value and its visibility, or a field it
    // turned off would come back on from the account
    return (
      this.theme?.header?.heroConfig?.[field] ?? this.account?.theme?.header?.heroConfig?.[field]
    );
  }

  // only an explicit false hides a field; an absent origin reads as enabled, matching the
  // publishing config's on-by-default behavior. The iframe embed renders no hero and is sent no
  // heroConfig, so it carries no flag to read and nothing is ever hidden for it
  private isHeroFieldVisible(field: HeroField): boolean {
    if (this.citOrigin === CitOrigin.IFRAME) {
      return true;
    }

    return this.getHeroField(field)?.[this.citOrigin] !== false;
  }

  // hero fields carry their value alongside their per-origin visibility, so a hidden field
  // reads as unset rather than as its value
  private getHeroFieldValue<K extends HeroField>(field: K): NonNullable<HeroConfig[K]>['value'] {
    return this.isHeroFieldVisible(field) ? this.getHeroField(field)?.value : undefined;
  }

  themeConfig(): AccountThemeConfig {
    return this.theme ?? this.account?.theme ?? DEFAULT_ACCOUNT_THEME;
  }

  // compressed so the theme can ride along in a query param without blowing up the URL length
  getCompressedThemeConfig(): string {
    return compressToEncodedURIComponent(JSON.stringify(this.themeConfig()));
  }

  isTestTheme(): boolean {
    return this.theme !== undefined;
  }
}
